import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../../../../ui/button';
import { uploadFileToFirebase } from '../../../../../utils/firebase';
import { useWorkspace } from '../../../../../context/WorkspaceContext';
import { FaPlay, FaPause, FaVideo, FaImage, FaUpload } from 'react-icons/fa';
import { BsCameraFill } from 'react-icons/bs';
import { MdError } from 'react-icons/md';

const CustomThumbnailSelector = ({ videoPath, onThumbnailSelect, currentThumbnail }) => {
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const fileInputRef = useRef(null); // Ref for hidden file input
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // For frame capture upload
  const [uploadProgress, setUploadProgress] = useState(0); // For frame capture upload
  const [isCustomUploading, setIsCustomUploading] = useState(false); // For custom file upload
  const [customUploadProgress, setCustomUploadProgress] = useState(0); // For custom file upload
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(currentThumbnail || null);
  const [showThumbnail, setShowThumbnail] = useState(!!currentThumbnail);
  const { activeWorkspace } = useWorkspace();
  
  // Load the video directly
  useEffect(() => {
    if (!videoPath) return;
    
    setIsLoading(true);
    setError(null);
    
    const captureFirstFrame = async (videoElement) => {
      // Use a micro-delay to ensure the frame is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!videoElement) return;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth || 320;
        canvas.height = videoElement.videoHeight || 180;
        const context = canvas.getContext('2d');
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const timestamp = new Date().getTime();
        const thumbnailFile = new File([blob], `first_frame_${timestamp}.jpg`, {
          type: 'image/jpeg'
        });

        // Temporarily set uploading state for visual feedback
        setIsUploading(true);
        setUploadProgress(0);

        const thumbnailUrl = await uploadFileToFirebase(
          thumbnailFile,
          'img',
          (progress) => setUploadProgress(progress),
          activeWorkspace.id
        );

        setThumbnailPreview(thumbnailUrl);
        setShowThumbnail(true);
        onThumbnailSelect({ imageUrl: thumbnailUrl });

      } catch (err) {
        console.error('Error capturing first frame:', err);
        // Don't set a blocking error, just log it. Maybe the video is problematic.
        // setError('Failed to capture initial thumbnail.');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    };

    const handleVideoLoad = () => {
      if (videoRef.current) {
        setDuration(videoRef.current.duration || 0);
        setIsLoading(false);

        // Capture the first frame if no current thumbnail exists
        if (!currentThumbnail && !thumbnailPreview) {
          // Seek to the beginning (time 0)
          videoRef.current.currentTime = 0;
          // Use 'seeked' event to ensure the frame is ready before capturing
          const onSeeked = () => {
            captureFirstFrame(videoRef.current);
            videoRef.current?.removeEventListener('seeked', onSeeked);
          };
          videoRef.current.addEventListener('seeked', onSeeked);
        }
      }
    };
    
    const handleVideoError = (e) => {
      console.error('Video loading error:', e);
      setError('Failed to load video. The video might be protected or unavailable.');
      setIsLoading(false);
    };
    
    // Store event handlers for cleanup reference
    let videoElement = null;
    
    if (videoRef.current) {
      videoElement = videoRef.current;
      // Set up event listeners
      videoElement.addEventListener('loadedmetadata', handleVideoLoad);
      videoElement.addEventListener('error', handleVideoError);
      
      // Set the video source
      videoElement.src = videoPath;
    }
    
    // Return cleanup function that uses locally stored video element reference
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('loadedmetadata', handleVideoLoad);
        videoElement.removeEventListener('error', handleVideoError);
      }
    };
  }, [videoPath, currentThumbnail, thumbnailPreview, activeWorkspace?.id, onThumbnailSelect]);
  
  // Handle time updates
  useEffect(() => {
    let videoElement = null;
    
    if (videoRef.current) {
      videoElement = videoRef.current;
      
      const handleTimeUpdate = () => {
        setCurrentTime(videoElement.currentTime);
      };
      
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        if (videoElement) {
          videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        }
      };
    }
  }, []);
  
  const togglePlayPause = () => {
    if (!videoRef.current) return;
    
    try {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        // Try to play and handle any autoplay restrictions
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Playback started successfully
            })
            .catch(err => {
              console.error('Playback failed:', err);
              // Handle autoplay restrictions
              setError('Playback failed. Try clicking the play button again.');
            });
        }
      }
      setIsPlaying(!isPlaying);
    } catch (err) {
      console.error('Error during play/pause:', err);
      setError('Failed to control video playback.');
    }
  };
  
  const seekVideo = (e) => {
    if (!videoRef.current || isLoading) return;
    
    try {
      const rect = e.target.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      videoRef.current.currentTime = percentage * duration;
    } catch (err) {
      console.error('Error seeking video:', err);
      setError('Failed to seek to the selected position.');
    }
  };
  
  // Toggle between video and thumbnail view
  const toggleThumbnailView = () => {
    setShowThumbnail(!showThumbnail);
    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  };
  
  // Using canvas.drawImage() to capture the frame
  const captureThumbnail = async () => {
    if (!videoRef.current) return;
  
    try {
      setIsUploading(true);
      setError(null);
  
      // Pause the video to capture the current frame
      videoRef.current.pause();
      setIsPlaying(false);
  
      // Create an off-screen canvas element
      const canvas = document.createElement('canvas');
      // Ensure the canvas matches the video dimensions
      canvas.width = videoRef.current.videoWidth || 320;
      canvas.height = videoRef.current.videoHeight || 180;
  
      const context = canvas.getContext('2d');
      // Draw the current video frame onto the canvas
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
  
      // Convert the canvas to a data URL (you can also convert directly to a blob)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
  
      // Convert the dataURL to a blob (this is one way to get around direct Blob conversion issues)
      const response = await fetch(dataUrl);
      const blob = await response.blob();
  
      // Create a File object from the Blob
      const timestamp = new Date().getTime();
      const thumbnailFile = new File([blob], `thumbnail_${timestamp}.jpg`, {
        type: 'image/jpeg'
      });
  
      // Upload the thumbnail to Firebase
      const thumbnailUrl = await uploadFileToFirebase(
        thumbnailFile,
        'img',
        (progress) => setUploadProgress(progress),
        activeWorkspace.id
      );
  
      // Set the thumbnail preview and show it
      setThumbnailPreview(thumbnailUrl);
      setShowThumbnail(true);
  
      // Call the callback with the thumbnail URL
      onThumbnailSelect({ imageUrl: thumbnailUrl });
    } catch (err) {
      console.error('Error capturing thumbnail:', err);
      setError('Failed to capture thumbnail. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // --- Custom Image Upload Handlers ---

  const handleUploadButtonClick = () => {
    // Trigger click on the hidden file input
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !activeWorkspace?.id) return;

    // Basic validation (optional: add more robust checks)
    if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file.');
        return;
    }

    setIsCustomUploading(true);
    setError(null);
    setCustomUploadProgress(0);

    try {
        // Upload the custom thumbnail to Firebase
        const thumbnailUrl = await uploadFileToFirebase(
            file,
            'img', // Assuming 'img' is your desired folder
            (progress) => setCustomUploadProgress(progress),
            activeWorkspace.id
        );

        // Set the thumbnail preview and show it
        setThumbnailPreview(thumbnailUrl);
        setShowThumbnail(true);

        // Call the callback with the thumbnail URL
        onThumbnailSelect({ imageUrl: thumbnailUrl });

    } catch (err) {
        console.error('Error uploading custom thumbnail:', err);
        setError('Failed to upload custom thumbnail. Please try again.');
    } finally {
        setIsCustomUploading(false);
        setCustomUploadProgress(0);
        // Reset file input value to allow uploading the same file again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }
  };

  // ------------------------------------

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === Infinity) return '0:00';
    
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const retryVideoLoad = () => {
    setIsLoading(true);
    setError(null);
    
    if (videoRef.current) {
      // Add cache-busting parameter to force a fresh load
      const cacheBuster = Date.now();
      const url = videoPath.includes('?') 
        ? `${videoPath}&cb=${cacheBuster}` 
        : `${videoPath}?cb=${cacheBuster}`;
        
      videoRef.current.src = url;
    }
  };
  
  return (
    <div className="flex flex-col border-2 border-gray-300 rounded-md">
      <div className="relative rounded-md overflow-hidden">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center bg-gray-100 rounded-md w-full h-32 mb-2">
            <div className="animate-pulse text-blue-500">Loading video...</div>
          </div>
        )}
        
        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center bg-red-50 rounded-md w-full p-4">
            <MdError className="text-red-500 text-2xl mb-2" />
            <p className="text-red-600 text-sm text-center mb-2">{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              type="button"
              onClick={retryVideoLoad}
            >
              Retry
            </Button>
          </div>
        )}
        
        {/* Thumbnail/Video toggle button */}
        {thumbnailPreview && !isLoading && !error && (
          <div className="flex justify-end mb-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={toggleThumbnailView}
              className="flex items-center gap-1"
            >
              {showThumbnail ? (
                <>
                  <FaVideo size={12} />
                  <span>Edit Thumbnail</span>
                </>
              ) : (
                <>
                  <FaImage size={12} />
                  <span>Show Thumbnail</span>
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Thumbnail preview */}
        {showThumbnail && thumbnailPreview && (
          <div className="relative">
            <img 
              src={thumbnailPreview} 
              alt="Selected thumbnail" 
              className="w-[100px] mx-auto rounded-md"
            />
          </div>
        )}
        
        {/* Video container for capturing */}
        <div 
          ref={videoContainerRef} 
          className={`relative ${isLoading || error || (showThumbnail && thumbnailPreview) ? 'hidden' : ''}`}
        >
          {/* Video element */}
          <video 
            ref={videoRef}
            className="w-[200px] mx-auto rounded-md"
            playsInline
            onClick={togglePlayPause}
            muted
            preload="metadata"
            crossOrigin="anonymous"
          />
        </div>
      </div>
      
      {/* Video controls - only show when viewing video */}
      {!error && !isLoading && (!showThumbnail || !thumbnailPreview) && (
        <div className="flex flex-col mt-3">
          <div className="flex items-center mb-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={togglePlayPause}
              disabled={isLoading || !!error || isUploading || isCustomUploading} // Disable during any upload
              className="flex items-center space-x-1"
            >
              {isPlaying ? (
                <>
                  <FaPause size={12} />
                  <span>Pause</span>
                </>
              ) : (
                <>
                  <FaPlay size={12} />
                  <span>Play</span>
                </>
              )}
            </Button>
            <span className="ml-3 text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          
          {/* Video progress/seek bar */}
          <div 
            className={`w-full h-2 bg-gray-200 rounded-full cursor-pointer ${isLoading || !!error ? 'opacity-50' : ''}`}
            onClick={seekVideo}
          >
            <div 
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          
          {/* Capture button */}
          <Button
            variant="primary"
            className="mt-4 flex items-center justify-center space-x-2"
            disabled={isUploading || isLoading || !!error || isCustomUploading} // Disable during any upload
            onClick={captureThumbnail}
          >
            {isUploading ? (
              `Uploading Thumbnail... ${uploadProgress}%`
            ) : (
              <>
                <BsCameraFill size={16} />
                <span className='!text-white'>Capture Current Frame as Thumbnail</span>
              </>
            )}
          </Button>
        </div>
      )}

      {/* Custom Upload Button and Input - Show always unless loading/error */}
      {!isLoading && !error && (
          <div className="mt-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }} // Hide the default input
                disabled={isUploading || isCustomUploading}
              />
              <Button
                variant="secondary" // Or choose another appropriate variant
                className="w-full flex items-center justify-center space-x-2"
                disabled={isUploading || isCustomUploading} // Disable during any upload
                onClick={handleUploadButtonClick}
                type="button"
              >
                {isCustomUploading ? (
                  `Uploading Custom Image... ${customUploadProgress}%`
                ) : (
                  <>
                    <FaUpload size={14} />
                    Upload Custom Image
                  </>
                )}
              </Button>
          </div>
      )}
    </div>
  );
};

export default CustomThumbnailSelector;